import { ApiFehler, type Fehlerantwort } from './fehler';
import { anmeldekopf, basisUrl, rufe } from './client';
import { alsEuro } from '../betrag';

/*
 * AUS DEM KONTRAKT — nur Typen. Eine Werteinfuhr aus @crm/kontrakte zoege zod
 * in das Buendel (siehe den Kopf von @crm/kontrakte, `buendeltrennung.test.ts`).
 * Die Beschreibung eines Berichts prueft die API; die Oberflaeche baut sie nur.
 */
import type {
  Abfrageergebnis,
  BerichtAenderung,
  BerichtAnlage,
  Belegart,
  Berichtsbeschreibung,
  Berichtsergebnis,
  Berichtsfreigabe,
  Berichtsabo,
  Berichtsaboeingabe,
  Berichtsversandauskunft,
  Berichtsversanduebersicht,
  Berichtsversandzeile,
  Berichtsvorlage,
  Zielwert,
  Zielwerteingabe,
  FehlendeKennzahl,
  Herkunft,
  Herkunftsanfrage,
  Herkunftsbeleg,
  Berichtsfilter,
  Berichtsvergleich,
  Berichtszeitraum,
  Darstellung,
  Aufteilungswert,
  Ergebnisaufteilung,
  Ergebniszeile,
  GespeicherterBericht,
  Gruppierungskennung,
  Katalogkennzahl,
  Kataloggruppierung,
  Kennzahlenkatalog,
  Kennzahlwert,
  RelativerZeitraum,
  Vergleichsart,
  Vergleichszeitraum,
} from '@crm/kontrakte';

export type {
  Abfrageergebnis,
  BerichtAenderung,
  BerichtAnlage,
  Belegart,
  Berichtsbeschreibung,
  Berichtsergebnis,
  Berichtsfreigabe,
  Berichtsabo,
  Berichtsaboeingabe,
  Berichtsversandauskunft,
  Berichtsversanduebersicht,
  Berichtsversandzeile,
  Berichtsvorlage,
  Zielwert,
  Zielwerteingabe,
  FehlendeKennzahl,
  Herkunft,
  Herkunftsanfrage,
  Herkunftsbeleg,
  Berichtsfilter,
  Berichtsvergleich,
  Berichtszeitraum,
  Darstellung,
  Aufteilungswert,
  Ergebnisaufteilung,
  Ergebniszeile,
  GespeicherterBericht,
  Gruppierungskennung,
  Katalogkennzahl,
  Kataloggruppierung,
  Kennzahlenkatalog,
  Kennzahlwert,
  RelativerZeitraum,
  Vergleichsart,
  Vergleichszeitraum,
};

/**
 * Eigene Auswertungen (AUS-16, Auftrag #23 und #24).
 *
 * Katalog und Abfrage rechnen auf dem Server; die Oberflaeche kennt keine
 * Kennzahl, die der Katalog ihr nicht nennt, und keine Zahl, die er ihr nicht
 * liefert (AUS-01).
 */

/** AUS-01: die Kennzahlen, die der Benutzer sehen darf, mit Definition. */
export function katalog(signal?: AbortSignal): Promise<Kennzahlenkatalog> {
  return rufe<Kennzahlenkatalog>('/auswertung/katalog', signal === undefined ? {} : { signal });
}

/** AUS-16, AUS-10: das Ergebnis einer Beschreibung, mit Stand und Unvollständigem. */
export function abfrage(beschreibung: Berichtsbeschreibung): Promise<Abfrageergebnis> {
  return rufe<Abfrageergebnis>('/auswertung/abfrage', { verfahren: 'POST', rumpf: beschreibung });
}

export function berichte(signal?: AbortSignal): Promise<readonly GespeicherterBericht[]> {
  return rufe<readonly GespeicherterBericht[]>('/auswertung/berichte', signal === undefined ? {} : { signal });
}

export function bericht(id: string, signal?: AbortSignal): Promise<GespeicherterBericht> {
  return rufe<GespeicherterBericht>(`/auswertung/berichte/${encodeURIComponent(id)}`, signal === undefined ? {} : { signal });
}

/**
 * Der Bericht samt Ergebnis mit den Rechten des Betrachters (Auftrag #27,
 * AUS-06). Fehlt ein Fachrecht, nennt `fehlend` die Kennzahl.
 */
export function berichtErgebnis(id: string, signal?: AbortSignal): Promise<Berichtsergebnis> {
  return rufe<Berichtsergebnis>(`/auswertung/berichte/${encodeURIComponent(id)}/ergebnis`, signal === undefined ? {} : { signal });
}

/** AUS-07: die Einzelbelege hinter einer Zelle, seitenweise, mit ihrer Summe. */
export function herkunft(anfrage: Herkunftsanfrage): Promise<Herkunft> {
  return rufe<Herkunft>('/auswertung/herkunft', { verfahren: 'POST', rumpf: anfrage });
}

/** Auftrag #28: die fertigen Berichte zum Übernehmen (AUS-02, AUS-03, AUS-04, AUS-21). */
export function vorlagen(signal?: AbortSignal): Promise<readonly Berichtsvorlage[]> {
  return rufe<readonly Berichtsvorlage[]>('/auswertung/vorlagen', signal === undefined ? {} : { signal });
}

/* ── Versand nach Zeitplan (Auftrag #30, AUS-15) ────────────────────────── */

export function versandAuskunft(bericht: string, signal?: AbortSignal): Promise<Berichtsversandauskunft> {
  return rufe<Berichtsversandauskunft>(`/auswertung/berichte/${encodeURIComponent(bericht)}/versand`, signal === undefined ? {} : { signal });
}

export function aboAnlegen(bericht: string, eingabe: Berichtsaboeingabe): Promise<Berichtsabo> {
  return rufe<Berichtsabo>(`/auswertung/berichte/${encodeURIComponent(bericht)}/versand`, {
    verfahren: 'POST',
    rumpf: eingabe,
  });
}

export function aboAendern(bericht: string, abo: string, eingabe: Berichtsaboeingabe): Promise<Berichtsabo> {
  return rufe<Berichtsabo>(
    `/auswertung/berichte/${encodeURIComponent(bericht)}/versand/${encodeURIComponent(abo)}`,
    { verfahren: 'PUT', rumpf: eingabe },
  );
}

export function aboLoeschen(bericht: string, abo: string): Promise<void> {
  return rufe<void>(`/auswertung/berichte/${encodeURIComponent(bericht)}/versand/${encodeURIComponent(abo)}`, {
    verfahren: 'DELETE',
  });
}

/** Für die Administration: alle Abos und die letzten Läufe. */
export function versandUebersicht(signal?: AbortSignal): Promise<Berichtsversanduebersicht> {
  return rufe<Berichtsversanduebersicht>('/auswertung/versand', signal === undefined ? {} : { signal });
}

const WOCHENTAGE = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];

/** Der Zeitplan in Worten: „montags um 07:00", „am 31. (Monatsende) um 18:00". */
export function zeitplanText(a: Pick<Berichtsaboeingabe, 'rhythmus' | 'wochentag' | 'monatstag' | 'uhrzeit'>): string {
  if (a.rhythmus === 'taeglich') return `täglich um ${a.uhrzeit} Uhr`;
  if (a.rhythmus === 'woechentlich') return `${(WOCHENTAGE[(a.wochentag ?? 1) - 1] ?? '').toLowerCase()}s um ${a.uhrzeit} Uhr`;
  return `monatlich am ${String(a.monatstag ?? 1)}.${(a.monatstag ?? 1) > 28 ? ' (sonst am Monatsende)' : ''} um ${a.uhrzeit} Uhr`;
}

export const ERGEBNISNAMEN: Readonly<Record<Berichtsversandzeile['ergebnis'], string>> = {
  versandt: 'in den Postausgang gestellt',
  uebersprungen: 'übersprungen',
  gescheitert: 'gescheitert',
};

/* ── Zielwerte (Auftrag #29, AUS-12) ─────────────────────────────────────── */

export function zielwerte(): Promise<readonly Zielwert[]> {
  return rufe<readonly Zielwert[]>('/auswertung/zielwerte');
}

export type Zielmerkmale = {
  readonly betreuer: readonly { readonly id: string; readonly name: string }[];
  readonly kundengruppen: readonly string[];
};

export function zielmerkmale(): Promise<Zielmerkmale> {
  return rufe<Zielmerkmale>('/auswertung/zielwerte/merkmale');
}

/** Anlegen oder für denselben Schlüssel ersetzen (Kennzahl, Periode, Betreuer/Kundengruppe). */
export function zielwertSpeichern(eingabe: Zielwerteingabe): Promise<Zielwert> {
  return rufe<Zielwert>('/auswertung/zielwerte', { verfahren: 'PUT', rumpf: eingabe });
}

export function zielwertLoeschen(id: string): Promise<void> {
  return rufe<void>(`/auswertung/zielwerte/${encodeURIComponent(id)}`, { verfahren: 'DELETE' });
}

export function berichtAnlegen(anlage: BerichtAnlage): Promise<GespeicherterBericht> {
  return rufe<GespeicherterBericht>('/auswertung/berichte', { verfahren: 'POST', rumpf: anlage });
}

export function berichtAendern(id: string, aenderung: BerichtAenderung): Promise<GespeicherterBericht> {
  return rufe<GespeicherterBericht>(`/auswertung/berichte/${encodeURIComponent(id)}`, {
    verfahren: 'PUT',
    rumpf: aenderung,
  });
}

export function berichtLoeschen(id: string): Promise<void> {
  return rufe<void>(`/auswertung/berichte/${encodeURIComponent(id)}`, { verfahren: 'DELETE' });
}

/** Die Formate des Exports (AUS-08); XLSX und PDF seit Auftrag #28. */
export type Exportformat = 'csv' | 'xlsx' | 'pdf';

/**
 * AUS-08: das Ergebnis als CSV, XLSX oder PDF mit Kopfblock.
 *
 * Nicht über `rufe` — die Antwort ist eine Datei und kein JSON. Eine
 * Ablehnung trägt trotzdem das Fehlerformat und wird zu einem `ApiFehler`,
 * damit die Maske sie wie jede andere Meldung zeigt.
 */
export async function exportDatei(
  format: Exportformat,
  titel: string | null,
  beschreibung: Berichtsbeschreibung,
  mitBelegen = false,
): Promise<{ readonly datei: Blob; readonly name: string }> {
  const antwort = await fetch(`${basisUrl()}/auswertung/export`, {
    method: 'POST',
    headers: { ...anmeldekopf(), 'content-type': 'application/json' },
    body: JSON.stringify({ titel, beschreibung, format, mitBelegen }),
  });
  if (!antwort.ok) {
    let rumpf: unknown;
    try {
      rumpf = await antwort.json();
    } catch {
      rumpf = undefined;
    }
    const fehler: Fehlerantwort =
      rumpf !== null && typeof rumpf === 'object' && 'kennung' in rumpf && 'meldung' in rumpf
        ? (rumpf as Fehlerantwort)
        : {
            kennung: 'unerwartete_antwort',
            meldung: {
              wasGeschah: 'Der Export konnte nicht erstellt werden.',
              auswirkung: 'Es wurde keine Datei gespeichert.',
              naechsterSchritt: 'Versuchen Sie es erneut.',
            },
            vorgang: '',
            verstoesse: [],
          };
    throw new ApiFehler(fehler, antwort.status);
  }
  const kopf = antwort.headers.get('content-disposition') ?? '';
  const name = /filename="([^"]+)"/.exec(kopf)?.[1] ?? `bericht.${format}`;
  return { datei: await antwort.blob(), name };
}

/** Die Freigabestufen in Worten (Auftrag #27). */
export const FREIGABENAMEN: Readonly<Record<Berichtsfreigabe, string>> = {
  privat: 'privat',
  team: 'Team',
  alle: 'alle',
};

/** Das Widget eines Berichts auf dem Arbeitsplatz (Auftrag #27). */
export function berichtswidget(id: string): string {
  return `bericht:${id}`;
}

/* ── Anzeige ─────────────────────────────────────────────────────────────── */

export const ZEITRAUMNAMEN: Readonly<Record<RelativerZeitraum, string>> = {
  laufendes_jahr: 'Laufendes Jahr',
  letzte_12_monate: 'Letzte 12 Monate',
  laufendes_quartal: 'Laufendes Quartal',
  letztes_quartal: 'Letztes Quartal',
  laufender_monat: 'Laufender Monat',
  vorjahr: 'Vorjahr',
  naechste_12_monate: 'Nächste 12 Monate',
};

const DATUM = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });

/** Ein Kalendertag der API ("2026-09-29") als "29.09.2026" — ohne Zeitzonenrechnung. */
export function tagText(tag: string): string {
  const [j, m, t] = tag.split('-');
  return `${t ?? ''}.${m ?? ''}.${j ?? ''}`;
}

const KURZMONATE = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'] as const;

/**
 * Auftrag CRM-60: eine Spanne knapp genug für das Berichtswidget —
 * „Jan–Sep 2026", „Sep 2026", „2026", „Okt 2025–Sep 2026", „2024–2025";
 * was nicht auf Monatsgrenzen liegt, in Tagen: „01.01.–05.10.2025".
 * `bis` zählt mit, wie überall in der Auswertung.
 */
export function spanneText(von: string, bis: string): string {
  const [vj = 0, vm = 1, vt = 1] = von.split('-').map(Number);
  const [bj = 0, bm = 1, bt = 1] = bis.split('-').map(Number);
  const monatsende = new Date(Date.UTC(bj, bm, 0)).getUTCDate();
  if (vt === 1 && bt === monatsende) {
    if (vm === 1 && bm === 12) return vj === bj ? String(vj) : `${String(vj)}–${String(bj)}`;
    const vorn = KURZMONATE[vm - 1] ?? '';
    const hinten = KURZMONATE[bm - 1] ?? '';
    if (vj !== bj) return `${vorn} ${String(vj)}–${hinten} ${String(bj)}`;
    return vm === bm ? `${vorn} ${String(vj)}` : `${vorn}–${hinten} ${String(vj)}`;
  }
  if (von === bis) return tagText(von);
  const vorn = tagText(von);
  return `${vj === bj ? vorn.slice(0, 6) : vorn}–${tagText(bis)}`;
}

export function zeitraumText(z: Berichtszeitraum): string {
  return z.art === 'relativ' ? ZEITRAUMNAMEN[z.wert] : `${tagText(z.von)} – ${tagText(z.bis)}`;
}

/** Ein Zeitpunkt der API in Ortszeit, für „Stand" und „geändert". */
export function zeitpunktText(iso: string): string {
  return DATUM.format(new Date(iso));
}

/* Eine Stelle nur, wo der Wert sie hat — die durchschnittliche Gruppengroesse (TAG-45). */
const ZAHL = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 1 });
const EURO = new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const PROZENT = new Intl.NumberFormat('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

/**
 * Ein Wert der API in deutscher Schreibweise.
 *
 * Die API liefert Festkommatext ("1234.50"); `Number` ist hier nur für das
 * SETZEN — gerechnet wird mit dem Wert nicht (RND-01).
 */
export function wertText(wert: string | null, einheit: Katalogkennzahl['einheit']): string {
  if (wert === null) return '—';
  const zahl = Number(wert);
  if (einheit === 'EUR') return alsEuro(wert);
  if (einheit === 'Prozent') return `${PROZENT.format(zahl)} %`;
  if (einheit === 'Tage') return `${PROZENT.format(zahl)} Tage`;
  if (einheit === 'Stunden') return `${EURO.format(zahl)} Std.`;
  return ZAHL.format(zahl);
}

/** Der Name eines Berichts, der noch keinen hat: „Umsatz netto und Buchungen je Monat". */
export function vorschlagstitel(
  kennzahlen: readonly (Katalogkennzahl | undefined)[],
  gruppierung: Kataloggruppierung | undefined,
  /* Auftrag CRM-46: „Teilnehmer je Monat nach Kundenart". */
  aufteilung?: Kataloggruppierung | undefined,
): string {
  const namen = kennzahlen.flatMap((k) => (k === undefined ? [] : [k.name]));
  /* Auftrag CRM-42: Bei mehr als drei Kennzahlen zaehlt der Titel — er hat hoechstens 120 Zeichen. */
  const name =
    namen.length === 0
      ? 'Bericht'
      : namen.length > 3
        ? `${namen[0]!}, ${namen[1]!} und ${String(namen.length - 2)} weitere`
        : aufzaehlung(namen);
  const je = gruppierung === undefined ? name : `${name} je ${gruppierung.name}`;
  return aufteilung === undefined ? je : `${je} nach ${aufteilung.name}`;
}

/** „A“, „A und B“, „A, B und C“. */
export function aufzaehlung(namen: readonly string[]): string {
  return namen.length <= 1 ? (namen[0] ?? '') : `${namen.slice(0, -1).join(', ')} und ${namen.at(-1)!}`;
}

/** Die Vergleichsarten in Worten (Auftrag #26). */
export const VERGLEICHSNAMEN: Readonly<Record<Vergleichsart, string>> = {
  vorjahr: 'Gleicher Zeitraum im Vorjahr',
  vor_zwei_jahren: 'Gleicher Zeitraum vor zwei Jahren',
  vorperiode: 'Vorperiode',
  frei: 'Frei wählen',
};

/** Die Veränderung in deutscher Schreibweise: „+12,5 %", „−3,0 Pkt.". */
export function veraenderungText(wert: string | null, quote: boolean): string {
  if (wert === null) return '—';
  const zahl = Number(wert);
  const zeichen = zahl > 0 ? '+' : zahl < 0 ? '−' : '±';
  return `${zeichen}${PROZENT.format(Math.abs(zahl))} ${quote ? 'Pkt.' : '%'}`;
}
